import { useState } from "react"
import { z } from "zod"

import { clampToInboxSize, fitsInRemaining, remainingCells } from "~/components/inbox/capacity"
import { api } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import { useSafeSessionStorage } from "~/hooks/useSafeLocalStorage"
import type { ActionResolution } from "~/server/api/routers/haki/types"
import type {
  CreateTileProposalPayload,
  CreateTilesProposalPayload,
  MoveTileProposalPayload,
  MoveTilesProposalPayload,
  UpdateTileProposalPayload,
} from "~/server/api/routers/haki/tools/propose-tile-action"

export async function refreshTileCaches(utils: ReturnType<typeof api.useUtils>): Promise<void> {
  await Promise.all([
    utils.haki.report.getInboxPinboard.invalidate(),
    utils.haki.report.getReport.invalidate(),
    utils.haki.report.listReports.invalidate(),
    utils.haki.actions.list.invalidate(),
  ])
}

/** A confirmed Studio/Inbox tile proposal, singular or batched. */
export type TileProposal =
  | { actionKind: "create_tile"; payload: CreateTileProposalPayload }
  | { actionKind: "create_tiles"; payload: CreateTilesProposalPayload }
  | { actionKind: "update_tile"; payload: UpdateTileProposalPayload }
  | { actionKind: "move_tile"; payload: MoveTileProposalPayload }
  | { actionKind: "move_tiles"; payload: MoveTilesProposalPayload }

const actionStatusSchema = z.enum(["pending", "done", "cancelled"])
type ActionStatus = z.infer<typeof actionStatusSchema>

function toRowId(value: string, label: string): number {
  const id = Number(value)
  if (!Number.isInteger(id) || id <= 0) throw new Error(`Invalid ${label}: "${value}"`)
  return id
}

interface ProposalDestination {
  target?: "inbox"
  reportId?: string
  newReportName?: string
}

function toMutationDestination(destination: ProposalDestination) {
  if (destination.target === "inbox") return { target: "inbox" as const }
  if (destination.reportId) {
    return { reportId: toRowId(destination.reportId, "report id") }
  }
  if (destination.newReportName) return { newReportName: destination.newReportName }
  throw new Error("Missing report target.")
}

export interface UseConfirmTileActionArgs {
  blockId: string
  proposal: TileProposal
  resolution?: ActionResolution
  /** Without a thread there's nothing to stamp — the outcome then lives only in sessionStorage. */
  threadId?: number
}

interface UseConfirmTileActionResult {
  status: ActionStatus
  isSubmitting: boolean
  doneReportId: number | null
  proposedReportName: string | undefined
  /** `move_tile` only — the destination grid forced a smaller size. */
  wasResized: boolean
  confirm: () => Promise<void>
  cancel: () => void
}

/**
 * Write path behind a tile proposal's confirm/cancel card. `propose_create_tile` writes nothing,
 * so this click is both the real create/update and the only signal Haki gets that it went
 * through — hence the outcome is persisted, not kept in component state.
 */
export function useConfirmTileAction({
  blockId,
  proposal,
  resolution,
  threadId,
}: UseConfirmTileActionArgs): UseConfirmTileActionResult {
  // Fallback for before the outcome is persisted, and for surfaces without a threadId.
  const [sessionStatus, setSessionStatus] = useSafeSessionStorage<ActionStatus>(
    `haki-action-${blockId}`,
    "pending",
    actionStatusSchema,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [savedReportId, setSavedReportId] = useState<number | null>(null)
  const [savedSizeChanged, setSavedSizeChanged] = useState(false)

  const actionTrace = { hakiBlockId: blockId, hakiThreadId: threadId }

  const utils = api.useUtils()
  const recordResolution = api.haki.report.recordActionResolution.useMutation()
  const createTile = api.haki.report.createTile.useMutation()
  const createTiles = api.haki.report.createTiles.useMutation()
  const updateTile = api.haki.report.updateTile.useMutation()
  const moveTileTo = api.haki.report.moveTileTo.useMutation()
  const moveTilesTo = api.haki.report.moveTilesTo.useMutation()
  const createInboxPinboardTile = api.haki.report.createInboxPinboardTile.useMutation()
  const status = resolution?.status ?? sessionStatus
  const reportsQuery = api.haki.report.listReports.useQuery(undefined, {
    enabled: proposal.actionKind !== "update_tile" && !!proposal.payload.reportId,
  })
  const inboxQuery = api.haki.report.getInboxPinboard.useQuery(undefined, {
    enabled:
      status === "pending" &&
      proposal.actionKind === "create_tile" &&
      proposal.payload.target === "inbox",
  })

  const reportNameById = (id: number) => reportsQuery.data?.find((r) => r.id === id)?.name

  // Fire-and-forget: the tile write already succeeded, so a failure here only costs Haki its
  // awareness of the outcome on the next turn.
  async function persistResolution(next: ActionResolution) {
    if (threadId == null) return
    try {
      await recordResolution.mutateAsync({ threadId, blockId, resolution: next })
      await utils.haki.getThread.invalidate({ threadId })
    } catch {
      // ignored — see above
    }
  }

  async function confirmCreate(p: CreateTileProposalPayload): Promise<ActionResolution> {
    if (p.target === "inbox") {
      const size = clampToInboxSize(p.size)
      if (!fitsInRemaining(size, remainingCells(inboxQuery.data?.tiles ?? []))) {
        throw new Error(
          "Your inbox is full. Remove a tile first, or ask Haki to add it to a Studio report instead.",
        )
      }
      const tile = await createInboxPinboardTile.mutateAsync({
        name: p.name,
        prompt: p.prompt,
        preferredType: p.preferredType,
        size,
        source: "haki_action",
        ...actionTrace,
      })
      return { status: "done", tileId: tile.tileId }
    }

    if (!p.reportId && !p.newReportName) throw new Error("Missing report target.")
    const tile = await createTile.mutateAsync({
      ...(p.reportId
        ? { reportId: toRowId(p.reportId, "report id") }
        : { newReportName: p.newReportName }),
      name: p.name,
      prompt: p.prompt,
      preferredType: p.preferredType,
      size: p.size,
      source: "haki_action",
      ...actionTrace,
    })
    return { status: "done", reportId: tile.reportId, reportName: tile.reportName, tileId: tile.id }
  }

  async function confirmUpdate(p: UpdateTileProposalPayload): Promise<ActionResolution> {
    const id = toRowId(p.id, "tile id")
    await updateTile.mutateAsync({
      id,
      name: p.name,
      prompt: p.prompt,
      preferredType: p.preferredType,
      size: p.size,
      source: "haki_action",
      ...actionTrace,
    })
    return { status: "done", tileId: id }
  }

  async function confirmCreateBatch(p: CreateTilesProposalPayload): Promise<ActionResolution> {
    const created = await createTiles.mutateAsync({
      tiles: p.tiles,
      source: "haki_action",
      ...actionTrace,
      ...toMutationDestination(p),
    })
    return {
      status: "done",
      tileIds: created.tileIds,
      ...(p.target === "inbox"
        ? {}
        : { reportId: created.reportId, reportName: created.reportName }),
    }
  }

  /** Capacity and the sm/md clamp are enforced server-side, so this just reports the outcome. */
  async function confirmMove(p: MoveTileProposalPayload): Promise<ActionResolution> {
    const tileId = toRowId(p.id, "tile id")
    const destination = await moveTileTo.mutateAsync({
      id: tileId,
      source: "haki_action",
      ...actionTrace,
      ...toMutationDestination(p),
    })
    return {
      status: "done",
      tileId,
      sizeChanged: destination.sizeChanged,
      // A move into the inbox reports the pinboard's own report row; only a Studio
      // destination is a report the user can be linked to.
      ...(p.target === "inbox"
        ? {}
        : {
            reportId: destination.destinationReportId,
            reportName: destination.destinationReportName,
          }),
    }
  }

  async function confirmMoveBatch(p: MoveTilesProposalPayload): Promise<ActionResolution> {
    const tileIds = p.tiles.map((tile) => toRowId(tile.id, "tile id"))
    const destination = await moveTilesTo.mutateAsync({
      tileIds,
      source: "haki_action",
      ...actionTrace,
      ...toMutationDestination(p),
    })
    return {
      status: "done",
      tileIds,
      sizeChanged: destination.sizeChanged,
      ...(p.target === "inbox"
        ? {}
        : {
            reportId: destination.destinationReportId,
            reportName: destination.destinationReportName,
          }),
    }
  }

  async function confirmProposal(): Promise<ActionResolution> {
    switch (proposal.actionKind) {
      case "create_tile":
        return confirmCreate(proposal.payload)
      case "create_tiles":
        return confirmCreateBatch(proposal.payload)
      case "update_tile":
        return confirmUpdate(proposal.payload)
      case "move_tile":
        return confirmMove(proposal.payload)
      case "move_tiles":
        return confirmMoveBatch(proposal.payload)
    }
  }

  async function confirm() {
    setIsSubmitting(true)
    try {
      const resolved = await confirmProposal()
      // One refresh for every path — `target` is model-supplied and often omitted on
      // updates, so a branch can't reliably tell whether it touched the inbox or a report.
      await refreshTileCaches(utils)
      setSavedReportId(resolved.reportId ?? null)
      setSavedSizeChanged(resolved.sizeChanged ?? false)
      setSessionStatus("done")
      void persistResolution(resolved)
    } catch (err) {
      showErrorToast(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  function cancel() {
    setSessionStatus("cancelled")
    void persistResolution({ status: "cancelled" })
  }

  return {
    status,
    isSubmitting,
    doneReportId: resolution?.reportId ?? savedReportId,
    proposedReportName:
      proposal.actionKind !== "update_tile" && proposal.payload.reportId
        ? reportNameById(Number(proposal.payload.reportId))
        : undefined,
    wasResized: resolution?.sizeChanged ?? savedSizeChanged,
    confirm,
    cancel,
  }
}
